import { GetQueryParams, GetQueryResponse } from '../../../types/api.type';
import { LessonPlanThemesCreate, Theme } from '../../../types/theme.type';
import { sfcApi } from '../../api';

export interface ThemesCreateUpdateValues {
  id?: number;
  name: string;
  is_public?: string;
  is_ase?: string;
  details: string;
  theme_lesson_plans: LessonPlanThemesCreate[];
  images?: (File | null)[];
  image_metadata?: { id?: number; is_featured: boolean; destroy: boolean }[];
}

export const themeApi = sfcApi.injectEndpoints({
  endpoints: (builder) => ({
    getThemes: builder.query<GetQueryResponse, GetQueryParams | void>({
      query: (params) => ({
        url: `themes${params?.scope ? `?scope=${params.scope}` : ''}`,
        method: 'POST',
        body: params?.data
      }),
      providesTags: ['Themes']
    }),
    
    getTheme: builder.query<{ theme: Theme }, number>({
      query: (id) => `themes/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Themes', id }]
    }),

    getThemesSimple: builder.query<Theme[], void>({
      query: () => ({
        url: `themes/simple`,
        method: 'POST'
      })
    }),

    createTheme: builder.mutation<{ theme: Theme }, ThemesCreateUpdateValues>({
      query: (data) => {
        const formData = new FormData();

        Object.entries(data).forEach(([key, value]) => {
          if (key !== 'images' && key !== 'image_metadata' && key !== 'id') {
            if (typeof value === 'object' && !Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else if (Array.isArray(value)) {
              value.forEach((v, index) => {
                if (typeof v === 'object' && v !== null) {
                  formData.append(`${key}[${index}]`, JSON.stringify(v));
                } else {
                  formData.append(`${key}[${index}]`, v);
                }
              });
            } else {
              formData.append(key, value as string | Blob);
            }
          }
        });

        if (data.image_metadata?.length) {
          data.image_metadata.forEach((metadata, index) => {
            let image = data.images?.[index];
            if (metadata.id) {
              formData.append(`image_metadata[${index}][id]`, String(metadata.id));
            }
            formData.append(`image_metadata[${index}][is_featured]`, String(metadata.is_featured ?? false));
            formData.append(`image_metadata[${index}][destroy]`, String(metadata.destroy ?? false));
            if (!image) {
              image = new File([], 'empty.png');
            }
            formData.append(`images`, image);
          });
        }

        return {
          url: 'themes/create',
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: ['Themes']
    }),

    updateTheme: builder.mutation<{ theme: Theme }, ThemesCreateUpdateValues>({
      query: (data) => {
        const formData = new FormData();
        const { id } = data;

        Object.entries(data).forEach(([key, value]) => {
          if (key !== 'images' && key !== 'image_metadata' && key !== 'id') {
            if (typeof value === 'object' && !Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else if (Array.isArray(value)) {
              value.forEach((v, index) => {
                if (typeof v === 'object' && v !== null) {
                  formData.append(`${key}[${index}]`, JSON.stringify(v));
                } else {
                  formData.append(`${key}[${index}]`, v);
                }
              });
            } else {
              formData.append(key, value as string | Blob);
            }
          }
        });

        if (data.image_metadata?.length) {
          data.image_metadata.forEach((metadata, index) => {
            let image = data.images?.[index];
            if (metadata.id) {
              formData.append(`image_metadata[${index}][id]`, String(metadata.id));
            }
            formData.append(`image_metadata[${index}][is_featured]`, String(metadata.is_featured ?? false));
            formData.append(`image_metadata[${index}][destroy]`, String(metadata.destroy ?? false));
            if (!image) {
              image = new File([], 'empty.png');
            }
            formData.append(`images`, image);
          });
        }

        return {
          url: `themes/update/${id}`,
          method: 'PUT',
          body: formData
        };
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Themes', id }, 'Themes']
    }),
    
    moveThemes: builder.mutation<{ response: any }, { data: any }>({
      query: (params) => ({
        url: `themes/change-positions`,
        method: 'PUT',
        body: { themes: params.data }
      }),
      invalidatesTags: ['Themes']
    }),
    
    deleteTheme: builder.mutation<{ theme: Theme }, number>({
      query: (id) => ({
        url: `themes/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Themes', id }, 'Themes']
    })
  })
});

export const {
  useGetThemesQuery,
  useGetThemesSimpleQuery,
  useGetThemeQuery,
  useCreateThemeMutation,
  useUpdateThemeMutation,
  useDeleteThemeMutation,
  useMoveThemesMutation
} = themeApi;
